import { SvgIconComponent } from '@mui/icons-material';
import { Button as MuiButton, ButtonProps as MuiButtonProps, alpha, lighten } from '@mui/material';
import { styled as muiStyled } from '@mui/material/styles';
import { EColorsKeys } from 'hooks/useThemedColor';
import { EColors, getColor } from 'theme';

type StyledButtonProps = {
    colorKey?: EColorsKeys;
};

const buttonStyle = ({ colorKey = EColors.primaryAction }: { colorKey?: EColorsKeys }) => ({
    '&.MuiButton-root': {
        textTransform: 'none',
    },

    '&.MuiButton-containedPrimary': {
        backgroundColor: getColor(colorKey),
        color: getColor(EColors.pureWhite),
        padding: '0.375rem 1rem',
        borderRadius: '0.25rem',
        fontSize: '0.875rem',
        fontWeight: '500',
        textTransform: 'capitalize',

        '&:hover': {
            backgroundColor: lighten(getColor(colorKey), 0.1),
        },
    },

    '&.MuiButton-outlinedPrimary': {
        borderColor: getColor(colorKey),
        color: getColor(colorKey),
        backgroundColor: getColor(EColors.pureWhite),
        padding: '0.375rem 1rem',
        borderRadius: '0.25rem',
        fontSize: '0.875rem',
        fontWeight: '500',
        border: `1px solid ${getColor(colorKey)}`,

        '&:hover': {
            borderColor: getColor(colorKey),
        },
    },

    '&.MuiButton-outlinedSecondary': {
        borderColor: getColor(EColors.pureWhite),
        color: getColor(EColors.totalAttendee),

        '&:hover': {
            // Need hover colors
            borderColor: 'transparent',
        },
    },

    '&.MuiButton-textPrimary': {
        color: getColor(colorKey),
        padding: '0.375rem 1rem',
        textTransform: 'capitalize',
    },

    '&.Mui-disabled': {
        color: alpha(getColor(EColors.pureBlack), 0.26),
        boxShadow: 'none',
        backgroundColor: alpha(getColor(EColors.pureBlack), 0.12),
        pointerEvents: 'none',
        cursor: 'default',
        borderColor: getColor(EColors.grey),
        padding: '0.375rem 1rem',
        borderRadius: '0.25rem',
        fontSize: '0.875rem',
        fontWeight: '500',
    },
});

const StyledButton = muiStyled(MuiButton, {
    shouldForwardProp: prop => prop !== 'colorKey',
})<StyledButtonProps>(buttonStyle);

interface IconButtonProps extends ButtonProps {
    leftIcon?: React.ComponentType<React.ComponentProps<SvgIconComponent>>;
    rightIcon?: React.ComponentType<React.ComponentProps<SvgIconComponent>>;
}

interface ButtonProps extends Omit<MuiButtonProps, 'onClick'> {
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
    colorKey?: EColorsKeys;
}

export const ButtonWithIcon = ({
    leftIcon: LeftIcon,
    rightIcon: RightIcon,
    children,
    variant = 'contained',
    colorKey = EColors.pureBlack,
    ...props
}: IconButtonProps) => {
    return (
        <StyledButton
            variant={variant}
            colorKey={colorKey}
            {...props}
            startIcon={LeftIcon && <LeftIcon fontSize="small" />}
            endIcon={RightIcon && <RightIcon fontSize="small" />}
        >
            {children}
        </StyledButton>
    );
};
